import { useState, useEffect, useCallback, useMemo, useImperativeHandle } from 'react';
import {
	FuseAuthProviderComponentProps,
	FuseAuthProviderState
} from '@fuse/core/FuseAuthProvider/types/FuseAuthTypes';
import { User } from '../../user';
import SupabaseAuthContext, { SupabaseAuthContextType } from './SupabaseAuthContext';
import { supabase } from '@/lib/supabase';
import { Sentry } from '@/lib/sentry';

const ADMIN_ROLES = ['admin', 'super_admin'];

// Defensive timeout for auth-path awaits. Supabase's getSession() can hang
// on Web Locks / internal refresh edge cases where the promise never
// resolves OR rejects; the profiles select can hang on network stalls.
// Either one leaves the FuseAuthProvider stuck on 'configuring' forever —
// try/catch doesn't help with promises that never settle. A Promise.race
// against a timeout forces the error path and lets us degrade to login.
const AUTH_STEP_TIMEOUT_MS = 5000;

function withAuthTimeout<T>(p: PromiseLike<T>, label: string): Promise<T> {
	return Promise.race([
		Promise.resolve(p),
		new Promise<T>((_, reject) => {
			setTimeout(() => reject(new Error(`${label} timed out after ${AUTH_STEP_TIMEOUT_MS}ms`)), AUTH_STEP_TIMEOUT_MS);
		})
	]);
}

/**
 * Look up the user's profile and verify they have an admin role.
 *
 * Uses the regular (anon) Supabase client — `profiles` is world-readable
 * via RLS, so no service role is needed for this lookup. Server-side admin
 * routes re-verify the role on every API call as defense in depth.
 */
async function getAdminUser(userId: string, email?: string): Promise<User | null> {
	const { data: profile } = await withAuthTimeout(
		supabase
			.from('profiles')
			.select('id, display_name, avatar_url, role')
			.eq('id', userId)
			.single(),
		'profiles lookup'
	);

	if (!profile || !ADMIN_ROLES.includes(profile.role)) {
		return null;
	}

	return {
		id: profile.id,
		role: [profile.role],
		displayName: profile.display_name ?? email ?? 'Admin',
		photoURL: profile.avatar_url ?? '',
		email: email ?? '',
		shortcuts: [],
		settings: {},
		loginRedirectUrl: '/dashboard'
	};
}

function SupabaseAuthProvider(props: FuseAuthProviderComponentProps) {
	const { ref, children, onAuthStateChanged } = props;

	const [authState, setAuthState] = useState<FuseAuthProviderState<User>>({
		authStatus: 'configuring',
		isAuthenticated: false,
		user: null
	});

	// Notify FuseAuthProvider of state changes
	useEffect(() => {
		if (onAuthStateChanged) {
			onAuthStateChanged(authState);
		}
	}, [authState, onAuthStateChanged]);

	// Auto-login: check for existing Supabase session on mount.
	// Any error here MUST fall through to unauthenticated — otherwise
	// authStatus stays 'configuring' forever and FuseAuthProvider's
	// isLoading never flips, leaving the app stuck on the splash spinner.
	useEffect(() => {
		const checkSession = async () => {
			try {
				const {
					data: { session }
				} = await withAuthTimeout(supabase.auth.getSession(), 'getSession');

				if (session?.user) {
					const adminUser = await getAdminUser(session.user.id, session.user.email);

					if (adminUser) {
						setAuthState({
							authStatus: 'authenticated',
							isAuthenticated: true,
							user: adminUser
						});
						return;
					}

					// Not an admin — sign them out
					await withAuthTimeout(supabase.auth.signOut(), 'signOut');
				}
			} catch (err) {
				console.error('[Admin auth] session check failed, falling back to login', err);
				Sentry.captureException(err);
			}

			setAuthState({
				authStatus: 'unauthenticated',
				isAuthenticated: false,
				user: null
			});
		};

		void checkSession();

		// Listen for auth state changes. Be conservative about flipping to
		// unauthenticated: only do it on an explicit SIGNED_OUT. Other
		// events with a null session (INITIAL_SESSION before hydration,
		// transient auto-refresh blips) used to flick users to the login
		// page mid-session — with Web Locks disabled (PR #29) those
		// events surface more often. checkSession() has already
		// established the truth on mount; if the session truly becomes
		// invalid, API calls will start returning 401 and the adminFetch
		// error surfaces there instead.
		const {
			data: { subscription }
		} = supabase.auth.onAuthStateChange(async (event, session) => {
			try {
				if (event === 'SIGNED_OUT') {
					setAuthState({
						authStatus: 'unauthenticated',
						isAuthenticated: false,
						user: null
					});
					return;
				}

				if (!session) {
					// Transient event with no session (INITIAL_SESSION w/ no
					// localStorage, auto-refresh intermediate state, etc.).
					// Don't touch authState — stay with whatever checkSession
					// established.
					return;
				}

				// Only refetch the profile on an explicit SIGNED_IN. TOKEN_REFRESHED
				// fires periodically (more often with Web Locks disabled) and the
				// user's identity / role doesn't change between refreshes — refetching
				// on every refresh was the source of the 5s "profiles lookup timed
				// out" errors. checkSession established the truth on mount; if admin
				// status truly changes mid-session, server-side admin routes 403 and
				// the adminFetch error surfaces there.
				if (event === 'SIGNED_IN') {
					const adminUser = await getAdminUser(session.user.id, session.user.email);

					if (adminUser) {
						setAuthState({
							authStatus: 'authenticated',
							isAuthenticated: true,
							user: adminUser
						});
					} else {
						await withAuthTimeout(supabase.auth.signOut(), 'signOut');
						setAuthState({
							authStatus: 'unauthenticated',
							isAuthenticated: false,
							user: null
						});
					}
				}
			} catch (err) {
				console.error('[Admin auth] auth state change handler failed', err);
				Sentry.captureException(err);
				// Do NOT flip to unauthenticated on unexpected errors here —
				// we'd risk kicking the user out on a transient network blip
				// during token refresh. checkSession already set the truth;
				// leave it alone.
			}
		});

		return () => subscription.unsubscribe();
	}, []);

	const signIn: SupabaseAuthContextType['signIn'] = useCallback(async (credentials) => {
		const { data, error } = await supabase.auth.signInWithPassword({
			email: credentials.email,
			password: credentials.password
		});

		if (error) throw error;

		const adminUser = await getAdminUser(data.user.id, data.user.email);

		if (!adminUser) {
			await supabase.auth.signOut();
			throw new Error('Unauthorized: admin access required');
		}

		setAuthState({
			authStatus: 'authenticated',
			isAuthenticated: true,
			user: adminUser
		});

		return { user: adminUser };
	}, []);

	const signOut: SupabaseAuthContextType['signOut'] = useCallback(async () => {
		await supabase.auth.signOut();
		setAuthState({
			authStatus: 'unauthenticated',
			isAuthenticated: false,
			user: null
		});
	}, []);

	const updateUser: SupabaseAuthContextType['updateUser'] = useCallback(async (_user) => {
		// No-op for now — admin profile updates can be added later
		return new Response(JSON.stringify(_user), { status: 200 });
	}, []);

	const authContextValue = useMemo(
		() =>
			({
				...authState,
				signIn,
				signOut,
				updateUser
			}) as SupabaseAuthContextType,
		[authState, signIn, signOut, updateUser]
	);

	useImperativeHandle(ref, () => ({
		signOut,
		updateUser
	}));

	return <SupabaseAuthContext value={authContextValue}>{children}</SupabaseAuthContext>;
}

export default SupabaseAuthProvider;
